import * as React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useState, useContext, useMemo } from 'react';

import type { LogViewMessage } from 'logview/types';

import type { ActiveMessageDetailsState, ActiveMessageDetailsStateSetter } from './MessageDetailsContext';
import MessageDetailsContext from './MessageDetailsContext';
import ListStateContext from './ListStateContext';

const findMessageCallback = (activeMessageDetailsState: ActiveMessageDetailsState) => (message: LogViewMessage) =>
  activeMessageDetailsState &&
  message.message._id === activeMessageDetailsState.id &&
  message.index === activeMessageDetailsState.glIndex;
const _nextMessageDetails = (
  visibleMessages: Array<LogViewMessage>,
  setActiveMessageDetailsState: ActiveMessageDetailsStateSetter,
  activeMessageDetailsState: ActiveMessageDetailsState,
) => {
  const index = visibleMessages.findIndex(findMessageCallback(activeMessageDetailsState));

  if (index >= 0 && index < visibleMessages.length - 1) {
    const {
      message: { _id: id },
      index: glIndex,
    } = visibleMessages[index + 1];

    return () => setActiveMessageDetailsState({ id, glIndex });
  }

  return undefined;
};

const _prevMessageDetails = (
  visibleMessages: Array<LogViewMessage>,
  setActiveMessageDetailsState: Dispatch<SetStateAction<ActiveMessageDetailsState>>,
  activeMessageDetailsState: ActiveMessageDetailsState,
) => {
  const index = visibleMessages.findIndex(findMessageCallback(activeMessageDetailsState));

  if (index > 0) {
    const {
      message: { _id: id },
      index: glIndex,
    } = visibleMessages[index - 1];

    return () => setActiveMessageDetailsState({ id, glIndex });
  }

  return undefined;
};

type Props = {
  children: React.ReactNode;
};

const MessageDetailsProvider = ({ children }: Props) => {
  const { pages } = useContext(ListStateContext);
  const [activeMessageDetailsState, setActiveMessageDetailsState] = useState<ActiveMessageDetailsState>(null);

  const contextValue = useMemo(() => {
    const visibleMessages: Array<LogViewMessage> = pages.flatMap(([, messages]) => messages).toJS();
    const activeMessageDetails =
      activeMessageDetailsState && visibleMessages.find(findMessageCallback(activeMessageDetailsState));
    const onSelectNextMessage = _nextMessageDetails(
      visibleMessages,
      setActiveMessageDetailsState,
      activeMessageDetailsState,
    );
    const onSelectPreviousMessage = _prevMessageDetails(
      visibleMessages,
      setActiveMessageDetailsState,
      activeMessageDetailsState,
    );

    return {
      activeMessageDetails,
      selectNextMessage: onSelectNextMessage,
      selectPrevMessage: onSelectPreviousMessage,
      setActiveMessageDetailsState,
    };
  }, [activeMessageDetailsState, pages]);

  return <MessageDetailsContext.Provider value={contextValue}>{children}</MessageDetailsContext.Provider>;
};

export default MessageDetailsProvider;
